import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { SEOHead } from "@/components/seo/SEOHead";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Search, Building2, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface NursingHome {
  nh_slug: string;
  center_name: string;
  standardized_address: string;
  county: string;
  claimed: boolean;
}

export default function GeneralClaimPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<NursingHome[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const navigate = useNavigate();

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      toast.error("Please enter a search term");
      return;
    }

    setIsSearching(true);
    setHasSearched(true);

    try {
      const { data, error } = await supabase
        .from("nursing_homes")
        .select("nh_slug, center_name, standardized_address, county, claimed")
        .or(`center_name.ilike.%${searchTerm}%,standardized_address.ilike.%${searchTerm}%,county.ilike.%${searchTerm}%`)
        .order('center_name');

      if (error) throw error;

      setSearchResults(data || []);
    } catch (error) {
      console.error("Error searching nursing homes:", error);
      toast.error("Failed to search nursing homes");
    } finally {
      setIsSearching(false);
    }
  };

  const handleClaimClick = (nursingHome: NursingHome) => {
    if (nursingHome.claimed) {
      toast.error("This nursing home has already been claimed");
      return;
    }
    navigate(`/claim/${nursingHome.nh_slug}`);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <Layout>
      <SEOHead 
        title="Claim Your Nursing Home Profile | Grandpal"
        description="Find and claim your nursing home profile on Grandpal to manage your information and connect with families looking for care."
      />

      <div className="container py-16 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 text-grandpal-navy">
            Claim Your Nursing Home Profile
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Take control of your nursing home's online presence. Update information, 
            manage photos, and connect directly with families seeking care.
          </p>
        </div>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Find Your Nursing Home
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label htmlFor="search">Search by name, address, or county</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    id="search"
                    placeholder="e.g., 'Sunset Manor' or 'Dublin' or 'Main Street'"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="flex-1"
                  />
                  <Button 
                    onClick={handleSearch}
                    disabled={isSearching}
                    className="bg-grandpal-green-dark hover:bg-grandpal-green-darkHover"
                  >
                    {isSearching ? "Searching..." : "Search"}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {hasSearched && (
          <Card>
            <CardHeader>
              <CardTitle>
                Search Results ({searchResults.length} found)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {searchResults.length === 0 ? (
                <div className="text-center py-8">
                  <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No nursing homes found</h3>
                  <p className="text-muted-foreground mb-4">
                    Try adjusting your search terms or check the spelling.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Can't find your nursing home? Contact our support team at{" "}
                    <a href="mailto:support@grandpal.co" className="text-grandpal-green-dark hover:underline">
                      support@grandpal.co
                    </a>
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {searchResults.map((nursingHome) => (
                    <div
                      key={nursingHome.nh_slug}
                      className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">
                            {nursingHome.center_name}
                          </h3>
                          <p className="text-muted-foreground">
                            {nursingHome.standardized_address}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {nursingHome.county}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          {nursingHome.claimed ? (
                            <div className="flex items-center gap-2 text-green-600">
                              <CheckCircle className="h-4 w-4" />
                              <span className="text-sm font-medium">Already Claimed</span>
                            </div>
                          ) : (
                            <Button
                              onClick={() => handleClaimClick(nursingHome)}
                              className="bg-grandpal-green-dark hover:bg-grandpal-green-darkHover"
                            >
                              Claim This Profile
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="mt-12 bg-grandpal-green-light/20 rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Benefits of Claiming Your Profile</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-grandpal-green-dark mt-0.5 flex-shrink-0" />
                <span>Update your facility information and photos</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-grandpal-green-dark mt-0.5 flex-shrink-0" />
                <span>Respond directly to family inquiries</span>
              </li>
            </ul>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-grandpal-green-dark mt-0.5 flex-shrink-0" />
                <span>Access qualified leads from families</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-grandpal-green-dark mt-0.5 flex-shrink-0" />
                <span>Highlight special features and amenities</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </Layout>
  );
}